import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import axios from 'axios';
import CONFIG from '../../config';
import Swal from 'sweetalert2';

const RunOfficerSimulator = ({ projectId, onActionSuccess }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [selectedCandidateId, setSelectedCandidateId] = useState('');

    useEffect(() => {
        if (isOpen && projectId) {
            fetchActiveCandidates();
            const interval = setInterval(fetchActiveCandidates, 5000);
            return () => clearInterval(interval);
        }
    }, [isOpen, projectId]);

    const fetchActiveCandidates = async () => {
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/candidates/live-run/${projectId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const list = resp.data.candidates || [];
            setCandidates(list);
            if (list.length > 0 && !selectedCandidateId) {
                setSelectedCandidateId(list[0].id);
            }
        } catch (err) {
            console.error('Failed to fetch candidates for simulator:', err);
        }
    };

    const handleAction = async (endpoint, data, successMsg) => {
        // 1. Check if candidate is selected
        if (!selectedCandidateId) {
            Swal.fire({
                title: 'No Candidate Selected',
                text: 'Select the candidate first',
                icon: 'warning',
                confirmButtonColor: 'var(--primary)'
            });
            return;
        }


        // 2. Check if race is active for all actions
        if (candidates.length === 0) {
            Swal.fire({
                title: 'Race Not Started',
                text: 'Cannot perform simulation actions while the race is not active.',
                icon: 'info',
                confirmButtonColor: 'var(--primary)'
            });
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.post(`${CONFIG.API_URL}/api/candidates/${endpoint}`, {
                candidateId: selectedCandidateId,
                ...data
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });



            if (onActionSuccess) onActionSuccess();

            const Toast = Swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });
            Toast.fire({ icon: 'success', title: successMsg });
            fetchActiveCandidates();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Action failed', 'error');
        }
    };

    return (
        <div style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '12px'
        }}>
            {isOpen && (
                <div style={{
                    width: '340px',
                    maxHeight: '480px',
                    overflowY: 'auto',
                    background: 'white',
                    border: '2px solid var(--primary)',
                    borderRadius: '20px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    padding: '24px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', color: 'var(--primary)' }}>
                        <Icon icon="ion:settings-sharp" width="22" height="22" />
                        <h3 style={{ fontSize: '14px', fontWeight: 800, margin: 0 }}>RUN CONTROL REMOTE</h3>
                    </div>

                    {!projectId ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', border: '1px dashed var(--border-light)', borderRadius: '12px' }}>
                            Please select a project to enable remote controls.
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gap: '16px' }}>
                            {/* Candidate Selector */}
                            <div>
                                <label style={{ fontSize: '11px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '6px', display: 'block', letterSpacing: '0.5px' }}>TARGET CANDIDATE</label>
                                <select
                                    value={selectedCandidateId}
                                    onChange={(e) => setSelectedCandidateId(e.target.value)}
                                    style={{
                                        width: '100%', padding: '10px 14px', borderRadius: '10px',
                                        border: '1px solid var(--border-light)', fontSize: '13px',
                                        fontFamily: 'inherit', background: '#fafafa', outline: 'none'
                                    }}
                                >
                                    <option value="">Select Candidate</option>
                                    {candidates.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.chest_number} — {c.full_name} ({c.laps_completed} Laps)
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Action Buttons */}
                            <div style={{ padding: '16px', background: 'rgba(128,0,0,0.02)', borderRadius: '12px', border: '1px solid var(--border-light)' }}>
                                <label style={{ fontSize: '11px', fontWeight: 800, color: 'var(--primary)', marginBottom: '12px', display: 'block', letterSpacing: '0.5px' }}>MANUAL OVERRIDES</label>
                                <div style={{ display: 'grid', gap: '10px' }}>
                                    <SimBtn
                                        onClick={() => handleAction('manual-lap', {}, '+1 Lap Recorded')}
                                        label="+1 LAP COMPLETED"
                                        color="#0ea5e9"
                                        icon="ion:add-circle"
                                    />
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <SimBtn
                                            onClick={() => handleAction('manual-move', { action: 'PASS' }, 'Candidate Passed!')}
                                            label="PASS / NEXT"
                                            color="#10b981"
                                            icon="ion:checkmark-circle"
                                        />
                                        <SimBtn
                                            onClick={() => handleAction('manual-move', { action: 'FAIL' }, 'Candidate Rejected')}
                                            label="FAIL / REJECT"
                                            color="#ef4444"
                                            icon="ion:close-circle"
                                        />
                                    </div>

                                </div>
                            </div>

                            <p style={{ fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center', margin: 0 }}>
                                Actions bypass RFID and update the leaderboard instantly.
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* FAB Toggle Button — using a reliable ion icon */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                title={isOpen ? 'Close Remote' : 'Open Run Control Remote'}
                style={{
                    width: '56px',
                    height: '56px',
                    background: isOpen ? '#6b7280' : 'var(--primary)',
                    color: 'white',
                    borderRadius: '16px',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 10px 25px rgba(128,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                }}
            >
                <Icon icon={isOpen ? 'ion:close' : 'ion:game-controller'} width="28" height="28" />
            </button>
        </div>
    );
};

const SimBtn = ({ onClick, label, color, icon, disabled }) => (
    <button
        disabled={disabled}
        onClick={onClick}
        style={{
            flex: 1,
            padding: '11px 8px',
            background: 'white',
            border: `1.5px solid ${color}`,
            color: color,
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: 800,
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.18s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            opacity: disabled ? 0.45 : 1,
            fontFamily: 'inherit'
        }}
        onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = color; e.currentTarget.style.color = 'white'; } }}
        onMouseLeave={e => { if (!disabled) { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = color; } }}
    >
        <Icon icon={icon} width="14" height="14" />
        {label}
    </button>
);

export default RunOfficerSimulator;
